/* eslint-disable @typescript-eslint/no-explicit-any */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useState, useRef } from "react";
import { ClientDatabase, stores } from "./databaseConstants";
import { getApiUrl } from "../utils/apiEndPoints";
export const useIndexedDB = (databaseName, tableNames) => {
    const [db, setDB] = useState(null);
    const [isDBConnecting, setIsDBConnecting] = useState(true);
    //const for once the database has finished populating
    const [isDBPopulated, setIsDBPopulated] = useState(false);
    const hasInitialized = useRef(false); // Ref to track initialization
    useEffect(() => {
        if (hasInitialized.current)
            return; // Skip if already initialized
        hasInitialized.current = true; // Mark as initialized
        const initDB = () => {
            const request = indexedDB.open(databaseName, ClientDatabase.version);
            // Handle database upgrade
            request.onupgradeneeded = () => {
                const database = request.result;
                //remove all stores and re-create and enforce getting new data from the db
                stores.forEach((store) => {
                    if (database.objectStoreNames.contains(store.name)) {
                        database.deleteObjectStore(store.name);
                    }
                });
                stores.forEach((store) => {
                    if (!database.objectStoreNames.contains(store.name)) {
                        const objectStore = database.createObjectStore(store.name, store.options);
                        store.indexes.forEach((index) => {
                            objectStore.createIndex(index.name, index.keyPath, index.options);
                        });
                    }
                });
            };
            request.onsuccess = () => __awaiter(void 0, void 0, void 0, function* () {
                const database = request.result;
                setDB(database);
                setIsDBConnecting(false);
                yield populateStores(database);
                setIsDBPopulated(true);
            });
            request.onerror = () => {
                console.error("Failed to open database:", request.error);
                setIsDBConnecting(false);
            };
        };
        const apiEndpoint = getApiUrl();
        const configEndpoint = `${apiEndpoint}Configuration/config`;
        // Compare api config to the database store config where the ConfigID = 1
        //================================================================================================
        //                      CONFIGURATION CHECK
        //================================================================================================
        const isConfigDifferent = (database) => __awaiter(void 0, void 0, void 0, function* () {
            var _a, _b;
            try {
                // Fetch the current configuration value from the config store where ConfigID = 1
                const transaction = database.transaction("config", "readonly");
                const objectStore = transaction.objectStore("config");
                let currentConfigID = (_b = (_a = localStorage.getItem("currentConfigID")) === null || _a === void 0 ? void 0 : _a.toLocaleLowerCase()) !== null && _b !== void 0 ? _b : undefined;
                const currentConfigRequest = objectStore.getAll();
                const currentConfig = yield new Promise((resolve, reject) => {
                    currentConfigRequest.onsuccess = () => resolve(currentConfigRequest.result);
                    currentConfigRequest.onerror = () => reject(currentConfigRequest.error);
                });
                if (currentConfigRequest.result.length > 0 && currentConfigRequest.result[0] && currentConfigRequest.result[0].ID) {
                    currentConfigID = currentConfigRequest.result[0].ID;
                }
                else {
                    currentConfigID = undefined;
                }
                // If currentConfig is undefined, it means the key does not exist
                if (currentConfig === undefined) {
                    console.warn("ConfigID ", currentConfigID, " does not exist in the config store.");
                    return true; // Treat as different to trigger population
                }
                // Fetch the new configuration value from the API endpoint
                const configResponse = yield fetch(configEndpoint);
                if (!configResponse.ok) {
                    throw new Error("Failed to fetch configuration data");
                }
                const newConfig = (yield configResponse.json());
                if (!localStorage.getItem("currentConfigID")) {
                    localStorage.setItem("currentConfigID", newConfig[0].ID);
                }
                let comparison = true;
                if (currentConfig.length === 0 && newConfig.length === 0) {
                    comparison = currentConfig[0].ID !== newConfig[0].ID;
                }
                return comparison;
            }
            catch (error) {
                console.error("Error in isConfigDifferent:", error);
                return false;
            }
        });
        const populateStores = (database) => __awaiter(void 0, void 0, void 0, function* () {
            const configDifferent = yield isConfigDifferent(database);
            if (configDifferent) {
                // clearing any existing stores and populating.
                for (const store of stores) {
                    try {
                        const transaction = database.transaction(store.name, "readwrite");
                        const objectStore = transaction.objectStore(store.name);
                        objectStore.clear();
                        yield transaction.oncomplete;
                    }
                    catch (error) {
                        console.error(`Error clearing store ${store.name}:`, error);
                    }
                }
                //populating the data
                for (const store of stores) {
                    try {
                        const response = yield fetch(`${apiEndpoint}${store.endpointUrl}`);
                        if (!response.ok) {
                            throw new Error(`Failed to fetch data for store ${store.name}`);
                        }
                        const data = yield response.json();
                        const transaction = database.transaction(store.name, "readwrite");
                        const objectStore = transaction.objectStore(store.name);
                        data.forEach((item) => {
                            objectStore.put(item);
                        });
                        yield transaction.oncomplete;
                    }
                    catch (error) {
                        console.error(`Error populating store ${store.name}:`, error);
                    }
                }
            }
        });
        initDB();
    }, [databaseName]);
    // Define the functions to interact with the database
    const getValue = useCallback((tableName, id) => __awaiter(void 0, void 0, void 0, function* () {
        if (!db)
            return null;
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(tableName, "readonly");
            const store = transaction.objectStore(tableName);
            const request = store.get(id);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }), [db]);
    const getValueByIndex = useCallback((tableName, indexName, indexValue) => __awaiter(void 0, void 0, void 0, function* () {
        if (!db)
            return null;
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(tableName, "readonly");
            const store = transaction.objectStore(tableName);
            const index = store.index(indexName);
            const request = index.get(indexValue);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }), [db]);
    const getAllValue = useCallback((tableName) => __awaiter(void 0, void 0, void 0, function* () {
        if (!db)
            return [];
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(tableName, "readonly");
            const store = transaction.objectStore(tableName);
            const request = store.getAll();
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }), [db]);
    const putValue = useCallback((tableName, value) => __awaiter(void 0, void 0, void 0, function* () {
        if (!db)
            return null;
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(tableName, "readwrite");
            const store = transaction.objectStore(tableName);
            const request = store.put(value);
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
    }), [db]);
    const putBulkValue = useCallback((tableName, values) => __awaiter(void 0, void 0, void 0, function* () {
        if (!db)
            return [];
        return new Promise((resolve, reject) => {
            const transaction = db.transaction(tableName, "readwrite");
            const store = transaction.objectStore(tableName);
            const results = [];
            values.forEach((value) => {
                const request = store.put(value);
                request.onsuccess = () => results.push(request.result);
                request.onerror = () => reject(request.error);
            });
            transaction.oncomplete = () => resolve(results);
        });
    }), [db]);
    const updateValue = useCallback(({ tableName, id, newItem, }) => {
        if (!db)
            return;
        const transaction = db.transaction(tableName, "readwrite");
        const store = transaction.objectStore(tableName);
        newItem.ID = id;
        store.put(newItem);
    }, [db]);
    const deleteValue = useCallback((tableName, id) => {
        if (!db)
            return null;
        const transaction = db.transaction(tableName, "readwrite");
        const store = transaction.objectStore(tableName);
        store.delete(id);
        return id;
    }, [db]);
    const deleteAll = useCallback((tableName) => {
        if (!db)
            return;
        const transaction = db.transaction(tableName, "readwrite");
        const store = transaction.objectStore(tableName);
        store.clear();
    }, [db]);
    return {
        getValue,
        getValueByIndex,
        getAllValue,
        putValue,
        putBulkValue,
        updateValue,
        deleteValue,
        deleteAll,
        isDBConnecting,
        isDBPopulated,
    };
};
